import type React from 'react';
import chevronDownIcon from '~/static/assets/icons/landing-page/chevron-down-icon.svg';
// Asset imports - bundler handles path resolution
import scheduleIcon from '~/static/assets/icons/landing-page/icons8-schedule.svg';
import transcriptPng from '~/static/assets/images/landing-page/transcript_image.png';
import transcriptWebp from '~/static/assets/images/landing-page/transcript_image.webp';
import transcriptPlaceholder from '~/static/assets/images/landing-page/transcript_image-placeholder.png';
import videoVersionPng from '~/static/assets/images/landing-page/video_version.png';
import videoVersionWebp from '~/static/assets/images/landing-page/video_version.webp';
import videoVersionPlaceholder from '~/static/assets/images/landing-page/video_version-placeholder.png';
import { AuthErrorBanner } from '../AuthErrorBanner';
//import { DownloadAppsSection } from '../DownloadAppsSection';
import { Footer } from '../Footer';
import { LazyImage } from '../LazyImage';

interface LandingPageProps {
  loginInProgress: boolean;
  onLogin: () => void;
  onStartMeeting?: () => void;
  onJoinMeeting?: () => void;
  authError: string | null;
  errorDismissed: boolean;
  handleErrorDismiss: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  loginInProgress,
  onLogin,
  onStartMeeting,
  onJoinMeeting,
  authError,
  errorDismissed,
  handleErrorDismiss,
}) => {
  const showBanner = !!authError && !errorDismissed;

  return (
    <div id="LandingPage">
      {showBanner && (
        <AuthErrorBanner
          error={authError || 'Login failed. Please try again.'}
          type="error"
          onDismiss={handleErrorDismiss}
        />
      )}
      <main className={`home loginPage ${showBanner ? 'withBanner' : ''}`}>
        <section className="home__content" aria-labelledby="hero-title">
          <div className="home__content__centered">
            {/* Centered Content - No background container */}
            <div className="loginPage__centeredContent">
              {/* Left Content */}
              <div className="loginPage__contentLeft">
                <div className="loginPage__aiLabel">AI MEETS</div>
                <h1 id="hero-title" className="loginPage__title">
                  <span className="loginPage__titleBrand shine-text">Sangoma Meet:</span>
                  <span className="loginPage__titleRest"> Simple Video Calls & Meetings</span>
                </h1>
                <p className="loginPage__description">
                  Reduce costs with Sangoma's reliable business communications platforms built
                  in-house. Our integrated approach combines voice, video, networking, and security
                  technologies for an exceptional experience.
                </p>

                {/* Actions */}
                {loginInProgress ? (
                  <output className="loginPage__loading" aria-live="polite">
                    Authentication in progress...
                  </output>
                ) : (
                  <div className="loginPage__actions">
                    <button
                      type="button"
                      onClick={onStartMeeting ?? onLogin}
                      className="loginPage__startMeetingButton"
                      aria-describedby="start-meeting-desc"
                    >
                      <img
                        src={scheduleIcon.src}
                        width={scheduleIcon.width}
                        height={scheduleIcon.height}
                        alt=""
                        className="buttonIcon"
                        aria-hidden="true"
                      />
                      Start a meeting
                    </button>
                    <div id="start-meeting-desc" className="sr-only">
                      Create and host a new video meeting
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (onJoinMeeting) {
                          onJoinMeeting();
                        } else {
                          onLogin();
                        }
                      }}
                      className="loginPage__joinMeetingButton"
                      aria-describedby="join-meeting-desc"
                    >
                      Join a meeting
                      <img
                        src={chevronDownIcon.src}
                        width={chevronDownIcon.width}
                        height={chevronDownIcon.height}
                        alt=""
                        className="buttonChevron"
                        aria-hidden="true"
                      />
                    </button>
                    <div id="join-meeting-desc" className="sr-only">
                      Join an existing video meeting with a meeting ID
                    </div>
                  </div>
                )}
              </div>

              {/* Right Content - Hero Image */}
              <div className="loginPage__contentRight">
                <div className="loginPage__imageContainer">
                  <figure
                    className="loginPage__mockup"
                    role="img"
                    aria-labelledby="hero-image-caption"
                  >
                    <LazyImage
                      src={videoVersionPng.src}
                      webpSrc={videoVersionWebp.src}
                      fallbackSrc={videoVersionPng.src}
                      placeholder={videoVersionPlaceholder.src}
                      width={videoVersionPng.width}
                      height={videoVersionPng.height}
                      alt="Screenshot of Sangoma Meet video call interface showing multiple participants in a professional meeting"
                      className="loginPage__mockupImage"
                      loading="eager"
                      decoding="async"
                    />
                    {/* Transcript Overlay */}
                    <div className="loginPage__transcriptOverlay">
                      <LazyImage
                        src={transcriptPng.src}
                        webpSrc={transcriptWebp.src}
                        fallbackSrc={transcriptPng.src}
                        placeholder={transcriptPlaceholder.src}
                        width={transcriptPng.width}
                        height={transcriptPng.height}
                        alt="Real-time meeting transcript feature showing automated speech-to-text conversion"
                        className="loginPage__transcriptImage"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <figcaption id="hero-image-caption" className="sr-only">
                      Sangoma Meet video conferencing interface demonstrating multi-participant
                      calls with real-time transcription capabilities
                    </figcaption>
                  </figure>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Download Apps Section
        <DownloadAppsSection />
        */}

        {/* Footer */}
        <Footer />
      </main>
    </div>
  );
};
